import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import Tooltip from '@mui/material/Tooltip';
import { AccountCircle } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AuthUtil from './Utils/AuthUtil';
import UserSelfServiceModal from './Modal/UserSelfServiceModal';
import ImpersonateUserModal from './Modal/ImpersonateUserModal';
import LanguageSwitcher from './LanguageSwitcher';
import './NavMenu.css';

const searchInputSx = {
    width: 130,
    '& .MuiOutlinedInput-root': {
        color: '#263238',
        backgroundColor: '#ffffff',
        borderRadius: '6px',
        boxShadow: '0 1px 3px rgba(31, 42, 55, 0.18)',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4D5061',
            borderWidth: 2,
        },
    },
    '& .MuiOutlinedInput-input::placeholder': { color: '#60707c', opacity: 1 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(31, 42, 55, 0.2)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4D5061' },
};

const searchButtonSx = {
    backgroundColor: '#4D5061',
    color: '#fff',
    textTransform: 'none',
    height: 40,
    '&:hover': { backgroundColor: '#65687c' },
};

const NavMenu = (props) => {
    const { setSnackbarProperties, toggleNavMenu } = props;
    const { t } = useTranslation();
    const [goToTaskId, setGoToTaskId] = useState('');
    const [goToRiskId, setGoToRiskId] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [openUserModal, setOpenUserModal] = useState(false);
    const [openImpersonateModal, setOpenImpersonateModal] = useState(false);
    const navigate = useNavigate();

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const username = useMemo(() => AuthUtil.getUsername(userToken), [userToken]);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);
    const isImpersonating = useMemo(() => AuthUtil.isImpersonating(userToken), [userToken]);

    // Nesting is rejected server-side, so the shortcut is hidden inside an impersonated session —
    // where the banner's "Return to my account" is the way out.
    const canImpersonate = isSuperAdmin && !isImpersonating;

    function goToTask () {
        if (goToTaskId !== null && goToTaskId !== '' && goToTaskId !== 0) {
            window.location.href = '/Task/' + goToTaskId;
            setGoToTaskId('');
        }
    }

    function goToRisk () {
        if (goToRiskId !== null && goToRiskId !== '' && goToRiskId !== 0) {
            window.location.href = '/Hazard/' + goToRiskId;
            setGoToRiskId('');
        }
    }

    const numericOnly = (value) => value === '' || /^[0-9]+$/.test(value);

    // position="static": Layout sticks the impersonation banner and this AppBar together as one
    // unit, so this must not establish its own sticky context.
    return (
        <AppBar position="static" elevation={1} sx={{ backgroundColor: '#99C20E' }}>
            <Toolbar variant="dense" sx={{ minHeight: 56, gap: 1 }}>
                <IconButton edge="start" color="inherit" onClick={toggleNavMenu} aria-label="menu">
                    <MenuIcon />
                </IconButton>
                <Box
                    component={Link}
                    to="/"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'inherit',
                        textDecoration: 'none',
                        gap: 0.5,
                        mr: 2,
                    }}
                >
                    <DoneOutlinedIcon fontSize="large" />
                    <Typography variant="h6" component="span" noWrap>CorrectTrack</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, ml: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                            size="small"
                            placeholder={t('nav.taskIdPlaceholder')}
                            value={goToTaskId}
                            onChange={(e) => { if (numericOnly(e.target.value)) setGoToTaskId(e.target.value); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') goToTask(); }}
                            sx={searchInputSx}
                        />
                        <Button size="small" onClick={goToTask} sx={searchButtonSx}>
                            {t('nav.goToTask')}
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                            size="small"
                            placeholder={t('nav.riskIdPlaceholder')}
                            value={goToRiskId}
                            onChange={(e) => { if (numericOnly(e.target.value)) setGoToRiskId(e.target.value); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') goToRisk(); }}
                            sx={searchInputSx}
                        />
                        <Button size="small" onClick={goToRisk} sx={searchButtonSx}>
                            {t('nav.goToRisk')}
                        </Button>
                    </Box>

                    <LanguageSwitcher />

                    {canImpersonate &&
                        <Tooltip title={t('nav.impersonate')}>
                            <IconButton
                                color="inherit"
                                aria-label={t('nav.impersonate')}
                                onClick={() => setOpenImpersonateModal(true)}
                            >
                                <SwitchAccountIcon sx={{ transform: 'scale(1.2)' }} />
                            </IconButton>
                        </Tooltip>
                    }

                    <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
                        <AccountCircle sx={{ transform: 'scale(1.3)' }} />
                    </IconButton>
                    <Menu
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        keepMounted
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                        <MenuItem disabled>{t('nav.loggedInAs', { username })}</MenuItem>
                        <MenuItem onClick={() => { setAnchorEl(null); setOpenUserModal(true); }}>{t('nav.profileSettings')}</MenuItem>
                        <MenuItem onClick={() => { setAnchorEl(null); navigate('/Logout'); }}>{t('nav.logout')}</MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
            <UserSelfServiceModal open={openUserModal} setSnackbarProperties={setSnackbarProperties} onClose={() => setOpenUserModal(false)} />
            <ImpersonateUserModal
                open={openImpersonateModal}
                setSnackbarProperties={setSnackbarProperties}
                onClose={() => setOpenImpersonateModal(false)}
            />
        </AppBar>
    );
};

NavMenu.propTypes = {
    toggleNavMenu: PropTypes.func.isRequired,
    setSnackbarProperties: PropTypes.func,
};
export default NavMenu;
